import React, { useState } from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import ICatalogItemFacet from "../../../models/ICatalogItemFacet";
import IPriceRangeFilter from "../../../models/IPriceRangeFilter";
import AttributeTypeEnum from "../../../models/AttributeTypeEnum";
import FacetFilterService, { SelectedAttributeFilters } from "@services/FacetFilterService";
import OwPriceRangeFilter from "../../shared/OwPriceRangeFilter/OwPriceRangeFilter";

type Props = {
    facets: ICatalogItemFacet[];
    selectedFilters: SelectedAttributeFilters;
    priceRange: IPriceRangeFilter | null;
    toggleValue: (name: string, value: string) => void;
    onPriceRangeSearch: (priceRange: IPriceRangeFilter) => void;
    clearAll: () => void;
};

/** Collapse-state key for the price section; attribute names come from user data and could collide otherwise. */
const PRICE_RANGE_SECTION_KEY = "__price_range__";

/**
 * Faceted attribute filter panel for the ecommerce category browse.
 * Values within an attribute OR together; attributes AND together (server-side semantics).
 * Selected values stay visible/checked even when the current counts no longer include them.
 * The price range section renders even when the category exposes no attribute facets.
 */
export const EcommerceFacetSidebar: React.FC<Props> = ({
    facets,
    selectedFilters,
    priceRange,
    toggleValue,
    onPriceRangeSearch,
    clearAll,
}) => {
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

    const hasSelections =
        Object.values(selectedFilters).some((values) => values.length > 0) || FacetFilterService.hasActivePriceRange(priceRange);

    const toggleSection = (name: string) => {
        setCollapsedSections((previous) => ({ ...previous, [name]: !previous[name] }));
    };

    const priceCollapsed = !!collapsedSections[PRICE_RANGE_SECTION_KEY];

    const valueLabel = (facet: ICatalogItemFacet, value: string) => {
        if (facet.type === AttributeTypeEnum.BOOLEAN) {
            return <FormattedMessage id={value === "true" ? "yes" : "no"} />;
        }

        return value;
    };

    return (
        <Container className="EcommerceFacetSidebar">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <FacetHeader className="mb-0">
                    <FormattedMessage id="filters" />
                </FacetHeader>
                {hasSelections ? (
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={clearAll}>
                        <FormattedMessage id="clear_filters" />
                    </button>
                ) : null}
            </div>

            <FacetSection>
                <FacetSectionHeader
                    type="button"
                    className="btn btn-link p-0 d-flex align-items-center justify-content-between w-100"
                    onClick={() => toggleSection(PRICE_RANGE_SECTION_KEY)}>
                    <span>
                        <FormattedMessage id="price_range" />
                    </span>
                    <i className={`fas fa-chevron-${priceCollapsed ? "down" : "up"}`} />
                </FacetSectionHeader>

                {!priceCollapsed ? (
                    <div className="mt-2">
                        <OwPriceRangeFilter
                            onSearch={onPriceRangeSearch}
                            initialMinPrice={priceRange?.minPrice ?? null}
                            initialMaxPrice={priceRange?.maxPrice ?? null}
                            compact
                        />
                    </div>
                ) : null}
            </FacetSection>

            {facets.map((facet) => {
                const selectedValues = selectedFilters[facet.name] || [];
                const collapsed = !!collapsedSections[facet.name];

                // Keep selected values visible even when the filtered counts omit them
                const orphanSelectedValues = selectedValues.filter(
                    (value) => !facet.values.some((facetValue) => facetValue.value === value)
                );

                return (
                    <FacetSection key={facet.name}>
                        <FacetSectionHeader
                            type="button"
                            className="btn btn-link p-0 d-flex align-items-center justify-content-between w-100"
                            onClick={() => toggleSection(facet.name)}>
                            <span>{facet.name}</span>
                            <i className={`fas fa-chevron-${collapsed ? "down" : "up"}`} />
                        </FacetSectionHeader>

                        {!collapsed ? (
                            <div className="mt-1">
                                {facet.values.map((facetValue) => {
                                    const checked = selectedValues.includes(facetValue.value);

                                    return (
                                        <FacetValueRow className="form-check" key={facetValue.value}>
                                            <label className="form-check-label">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleValue(facet.name, facetValue.value)}
                                                />
                                                {valueLabel(facet, facetValue.value)} <FacetCount>({facetValue.count})</FacetCount>
                                            </label>
                                        </FacetValueRow>
                                    );
                                })}

                                {orphanSelectedValues.map((value) => (
                                    <FacetValueRow className="form-check" key={value}>
                                        <label className="form-check-label">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={true}
                                                onChange={() => toggleValue(facet.name, value)}
                                            />
                                            {valueLabel(facet, value)} <FacetCount>(0)</FacetCount>
                                        </label>
                                    </FacetValueRow>
                                ))}
                            </div>
                        ) : null}
                    </FacetSection>
                );
            })}
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
`;

const FacetHeader = styled.h6`
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const FacetSection = styled.div`
    padding: 0.75rem 0;
    border-top: 1px solid var(--bs-border-color, #dee2e6);
`;

const FacetSectionHeader = styled.button`
    font-weight: 600;
    color: inherit;
    text-decoration: none;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }

    i {
        font-size: 0.75rem;
    }
`;

const FacetValueRow = styled.div`
    label {
        cursor: pointer;
        user-select: none;
    }
`;

const FacetCount = styled.span`
    color: var(--bs-secondary-color, #6c757d);
    font-size: 0.85em;
`;
